import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import StatCard from "../components/StatCard";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { getAttendanceFeatureLabel, getEnabledAttendanceOptions } from "../lib/attendance";
import { formatDateTime, formatHours, getErrorMessage } from "../lib/formatters";


const navItems = [
  { path: "/company/dashboard", label: "Company Dashboard", shortLabel: "HOME", testId: "company-dashboard" },
  { path: "/company/employees", label: "Employees", shortLabel: "PEOPLE", testId: "company-employees" },
  { path: "/company/attendance", label: "Attendance Monitor", shortLabel: "TRACK", testId: "company-attendance" },
  { path: "/company/approvals", label: "Approvals", shortLabel: "QUEUE", testId: "company-approvals" },
  { path: "/company/kiosk", label: "Kiosk QR", shortLabel: "QR", testId: "company-kiosk" },
  { path: "/company/leave-settings", label: "Leave Settings", shortLabel: "LEAVE", testId: "company-leave-settings" },
  { path: "/company/reports", label: "Reports", shortLabel: "XLSX", testId: "company-reports" },
  { path: "/company/settings", label: "Settings", shortLabel: "CONFIG", testId: "company-settings" },
];

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});


export default function EmployeeProfilePage() {
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const { user, tenant, featureFlags, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [reference, setReference] = useState({ departments: [], teams: [], shifts: [], locations: [], business_units: [], leave_types: [] });
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState(null);
  const [passwordResetResult, setPasswordResetResult] = useState(null);
  const [showLifecycleModal, setShowLifecycleModal] = useState(false);
  const [deactivationReason, setDeactivationReason] = useState("");
  const [lifecycleBusy, setLifecycleBusy] = useState(false);

  const attendanceOptions = useMemo(() => getEnabledAttendanceOptions(featureFlags), [featureFlags]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [profileResponse, settingsResponse, reportResponse] = await Promise.all([
        api.get(`/employees/${employeeId}`),
        api.get("/company/settings"),
        api.get("/reports/summary", { params: { period: "monthly", anchor_date: new Date().toLocaleDateString("en-CA"), employee_id: employeeId } }),
      ]);
      setProfile(profileResponse.data);
      setReference({
        departments: settingsResponse.data.departments,
        teams: settingsResponse.data.teams,
        shifts: settingsResponse.data.shifts,
        locations: settingsResponse.data.locations,
        business_units: settingsResponse.data.business_units,
        leave_types: settingsResponse.data.leave_types,
      });
      setSummary(reportResponse.data.summary);
      setForm({
        phone: profileResponse.data.phone || "",
        role: profileResponse.data.role,
        department_id: profileResponse.data.department_id,
        team_id: profileResponse.data.team_id,
        shift_id: profileResponse.data.shift_id,
        location_id: profileResponse.data.location_id,
        business_unit_id: profileResponse.data.business_unit_id,
        aadhar_number: profileResponse.data.aadhar_number || "",
        profile_picture_data: profileResponse.data.profile_picture_data || "",
        manager_user_id: profileResponse.data.manager_user_id || "",
        assigned_attendance_feature: profileResponse.data.assigned_attendance_feature,
      });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const dataUrl = await fileToDataUrl(file);
    setForm((current) => ({ ...current, profile_picture_data: dataUrl }));
  };

  const handleSave = async () => {
    try {
      await api.put(`/employees/${employeeId}`, form);
      toast.success("Employee profile updated");
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleResetPassword = async () => {
    try {
      const response = await api.post(`/employees/${employeeId}/reset-password`, { send_email: false });
      setPasswordResetResult({ employeeName: profile.name, temporaryPassword: response.data.temporary_password });
      toast.success("Temporary password generated");
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const isInactive = (profile?.status || (profile?.active === false ? "inactive" : "active")) === "inactive";

  const handleDeactivate = async () => {
    setLifecycleBusy(true);
    try {
      const response = await api.post(`/employees/${employeeId}/deactivate`, { reason: deactivationReason || null });
      setProfile((current) => ({ ...current, ...response.data }));
      toast.success("Employee deactivated");
      setShowLifecycleModal(false);
      setDeactivationReason("");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLifecycleBusy(false);
    }
  };

  const handleReactivate = async () => {
    setLifecycleBusy(true);
    try {
      const response = await api.post(`/employees/${employeeId}/reactivate`);
      setProfile((current) => ({ ...current, ...response.data }));
      toast.success("Employee reactivated");
      setShowLifecycleModal(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLifecycleBusy(false);
    }
  };

  return (
    <AppShell
      title={loading ? "Employee profile" : profile?.name || "Employee profile"}
      subtitle="View and update employee details, profile picture, attendance assignment, and password access from one profile page."
      navItems={navItems}
      user={user}
      tenant={tenant}
      onLogout={logout}
      actions={<Button type="button" variant="outline" className="rounded-2xl" onClick={() => navigate('/company/employees')} data-testid="employee-profile-back-button">Back to directory</Button>}
    >
      <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]" data-testid="employee-profile-layout">
        <div className="space-y-6">
          <Card className="border-slate-200/70 bg-white/90" data-testid="employee-profile-summary-card">
            <CardHeader>
              <CardTitle data-testid="employee-profile-summary-title">Employee profile</CardTitle>
              <CardDescription data-testid="employee-profile-summary-description">Profile picture, identity, and assignment details for this employee.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {profile?.profile_picture_data ? <img src={profile.profile_picture_data} alt={profile.name} className="h-40 w-40 rounded-[28px] object-cover" data-testid="employee-profile-image" /> : <div className="flex h-40 w-40 items-center justify-center rounded-[28px] bg-[#0D1B2A] text-4xl font-bold text-white" data-testid="employee-profile-image-fallback">{profile?.name?.slice(0, 1)}</div>}
              <div className="flex items-center gap-3" data-testid="employee-profile-status-row">
                <span className="text-sm font-semibold text-slate-900">Employee Status:</span>
                <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${isInactive ? "bg-slate-200 text-slate-600" : "bg-emerald-100 text-emerald-700"}`} data-testid="employee-profile-status-badge">{isInactive ? "Inactive" : "Active"}</span>
              </div>
              {isInactive && profile?.deactivated_at ? <p className="text-xs text-slate-500" data-testid="employee-profile-deactivated-info">Deactivated on {formatDateTime(profile.deactivated_at)}{profile.deactivation_reason ? ` • ${profile.deactivation_reason}` : ""}</p> : null}
              <div className="grid gap-3 text-sm text-slate-600 md:grid-cols-2">
                <p data-testid="employee-profile-code"><span className="font-semibold text-slate-900">Employee ID:</span> {profile?.employee_code}</p>
                <p data-testid="employee-profile-role"><span className="font-semibold text-slate-900">Role:</span> {profile?.role?.replaceAll('_', ' ')}</p>
                <p data-testid="employee-profile-location"><span className="font-semibold text-slate-900">Location:</span> {profile?.location_name || '—'}</p>
                <p data-testid="employee-profile-business-unit"><span className="font-semibold text-slate-900">Business Unit:</span> {profile?.business_unit_name || '—'}</p>
                <p data-testid="employee-profile-aadhar"><span className="font-semibold text-slate-900">Aadhar:</span> {profile?.masked_aadhar_number || '—'}</p>
                <p data-testid="employee-profile-attendance-method"><span className="font-semibold text-slate-900">Attendance:</span> {profile?.assigned_attendance_label}</p>
              </div>
              <div className="grid gap-4 md:grid-cols-3" data-testid="employee-profile-stats-grid">
                <StatCard label="Rows" value={summary?.total_rows || 0} accent="#0D1B2A" helper="Current report rows" testId="employee-profile-stat-rows" />
                <StatCard label="Hours" value={formatHours(summary?.hours_worked || 0)} accent="#F4A300" helper="Hours in current report" testId="employee-profile-stat-hours" />
                <StatCard label="Late" value={summary?.late_count || 0} accent="#F59E0B" helper="Late marks in current report" testId="employee-profile-stat-late" />
              </div>
              <div className="border-t border-slate-100 pt-4" data-testid="employee-lifecycle-block">
                {isInactive ? (
                  <Button type="button" className="rounded-2xl bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => setShowLifecycleModal(true)} data-testid="employee-reactivate-button">Reactivate Employee</Button>
                ) : (
                  <Button type="button" variant="outline" className="rounded-2xl border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => setShowLifecycleModal(true)} data-testid="employee-deactivate-button">Deactivate Employee</Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Dialog open={showLifecycleModal} onOpenChange={(open) => { setShowLifecycleModal(open); if (!open) setDeactivationReason(""); }}>
            <DialogContent className="max-w-lg rounded-[28px] border border-[#0D1B2A]/10 bg-white" data-testid="employee-lifecycle-modal">
              <DialogHeader>
                <DialogTitle data-testid="employee-lifecycle-modal-title">{isInactive ? "Reactivate this employee?" : "Deactivate this employee?"}</DialogTitle>
                <DialogDescription data-testid="employee-lifecycle-modal-description">
                  {isInactive
                    ? "This will restore access to Tarka HR. Previous historical records remain unchanged."
                    : "Their access to Tarka HR will be stopped immediately. Their attendance, leave, and other historical records will be retained."}
                </DialogDescription>
              </DialogHeader>
              {!isInactive ? (
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Reason (optional)</label>
                  <Input value={deactivationReason} onChange={(event) => setDeactivationReason(event.target.value)} placeholder="e.g. Resigned, contract ended" data-testid="employee-deactivate-reason-input" />
                </div>
              ) : null}
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" className="rounded-2xl" onClick={() => setShowLifecycleModal(false)} data-testid="employee-lifecycle-cancel-button">Cancel</Button>
                {isInactive ? (
                  <Button type="button" className="rounded-2xl bg-emerald-600 text-white hover:bg-emerald-700" disabled={lifecycleBusy} onClick={handleReactivate} data-testid="employee-lifecycle-confirm-reactivate">{lifecycleBusy ? "Working…" : "Reactivate Employee"}</Button>
                ) : (
                  <Button type="button" className="rounded-2xl bg-rose-600 text-white hover:bg-rose-700" disabled={lifecycleBusy} onClick={handleDeactivate} data-testid="employee-lifecycle-confirm-deactivate">{lifecycleBusy ? "Working…" : "Deactivate Employee"}</Button>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Card className="border-slate-200/70 bg-white/90" data-testid="employee-profile-password-card">
            <CardHeader>
              <CardTitle data-testid="employee-profile-password-title">Password access</CardTitle>
              <CardDescription data-testid="employee-profile-password-description">Generate a temporary password and copy it once for secure handoff.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button type="button" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" onClick={handleResetPassword} data-testid="employee-profile-reset-password-button">Reset password</Button>
            </CardContent>
          </Card>

          <Card className="border-slate-200/70 bg-white/90" data-testid="employee-profile-leave-card">
            <CardHeader>
              <CardTitle data-testid="employee-profile-leave-title">Leave assignments</CardTitle>
              <CardDescription data-testid="employee-profile-leave-description">Current leave buckets assigned to this employee.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {(profile?.leave_assignments || []).map((item) => (
                <div key={item.id || item.leave_type_code} className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid={`employee-profile-leave-item-${item.leave_type_code}`}>
                  <p className="font-semibold text-slate-900">{item.leave_type_name}</p>
                  <p className="mt-1 text-sm text-slate-500">Remaining {item.remaining} • Used {item.used}</p>
                </div>
              ))}
              {!(profile?.leave_assignments || []).length ? <p className="text-sm text-slate-400" data-testid="employee-profile-leave-empty">No leave assignments available yet.</p> : null}
            </CardContent>
          </Card>
        </div>

        <Card className="border-slate-200/70 bg-white/90" data-testid="employee-profile-edit-card">
          <CardHeader>
            <CardTitle data-testid="employee-profile-edit-title">Edit employee</CardTitle>
            <CardDescription data-testid="employee-profile-edit-description">Update missing fields, profile picture, organisation mapping, and attendance assignment.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <Input value={profile?.name || ""} disabled data-testid="employee-profile-name-input" />
            <Input value={profile?.email || ""} disabled data-testid="employee-profile-email-input" />
            <Input value={form?.phone || ""} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} placeholder="Phone" data-testid="employee-profile-phone-input" />
            <Input value={form?.aadhar_number || ""} onChange={(event) => setForm((current) => ({ ...current, aadhar_number: event.target.value }))} placeholder="Aadhar number" data-testid="employee-profile-aadhar-input" />
            <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={form?.department_id || ""} onChange={(event) => setForm((current) => ({ ...current, department_id: event.target.value }))} data-testid="employee-profile-department-select">
              {reference.departments.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
            <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={form?.team_id || ""} onChange={(event) => setForm((current) => ({ ...current, team_id: event.target.value }))} data-testid="employee-profile-team-select">
              {reference.teams.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
            <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={form?.location_id || ""} onChange={(event) => setForm((current) => ({ ...current, location_id: event.target.value }))} data-testid="employee-profile-location-select">
              {reference.locations.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
            <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={form?.business_unit_id || ""} onChange={(event) => setForm((current) => ({ ...current, business_unit_id: event.target.value }))} data-testid="employee-profile-business-unit-select">
              {reference.business_units.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
            <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={form?.shift_id || ""} onChange={(event) => setForm((current) => ({ ...current, shift_id: event.target.value }))} data-testid="employee-profile-shift-select">
              {reference.shifts.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
            <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={form?.assigned_attendance_feature || ""} onChange={(event) => setForm((current) => ({ ...current, assigned_attendance_feature: event.target.value }))} data-testid="employee-profile-attendance-select">
              {attendanceOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 md:col-span-2" data-testid="employee-profile-picture-upload-card">
              <p className="text-sm font-semibold text-slate-900">Profile picture</p>
              <p className="mt-1 text-xs text-slate-500">Upload or replace the profile picture used for future photo-scanner verification.</p>
              <Input type="file" accept="image/png,image/jpeg,image/webp" className="mt-3" onChange={handleProfilePictureUpload} data-testid="employee-profile-picture-upload-input" />
            </div>
            <div className="flex flex-wrap justify-end gap-3 md:col-span-2">
              <Button type="button" variant="outline" className="rounded-2xl" onClick={() => navigate('/company/employees')} data-testid="employee-profile-cancel-button">Back</Button>
              <Button type="button" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" onClick={handleSave} data-testid="employee-profile-save-button">Save profile</Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={Boolean(passwordResetResult)} onOpenChange={(open) => { if (!open) setPasswordResetResult(null); }}>
        <DialogContent className="max-w-lg rounded-[28px] border border-[#0D1B2A]/10 bg-white">
          <DialogHeader>
            <DialogTitle data-testid="employee-profile-password-modal-title">Temporary password ready</DialogTitle>
            <DialogDescription data-testid="employee-profile-password-modal-description">Copy this password now. It will not be shown again after this modal is closed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4" data-testid="employee-profile-password-modal">
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
              <p className="font-semibold text-slate-900">{passwordResetResult?.employeeName}</p>
              <div className="mt-3 rounded-2xl border border-dashed border-[#F4A300]/35 bg-white px-4 py-3 text-lg font-bold tracking-[0.08em] text-[#0D1B2A]" data-testid="employee-profile-password-modal-value">{passwordResetResult?.temporaryPassword}</div>
            </div>
            <div className="flex flex-wrap justify-end gap-3">
              <Button type="button" variant="outline" className="rounded-2xl" onClick={() => navigator.clipboard.writeText(passwordResetResult?.temporaryPassword || "")} data-testid="employee-profile-password-copy-button">Copy Password</Button>
              <Button type="button" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" onClick={() => setPasswordResetResult(null)} data-testid="employee-profile-password-close-button">Done</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}